import { useRef, useState } from 'react';
import {
  analyzeWorkoutImage,
  cancelWorkoutImport,
  confirmWorkoutImport,
  Workout,
  WorkoutAnalysis,
  WorkoutImportDraft
} from '../api/client';
import { haptic } from '../lib/telegram';
import './ImageUpload.css';

interface ImageUploadProps {
  userId: number;
  onWorkoutCreated: (workout: Workout) => void;
  onCancel: () => void;
}

type UploadState = 'select' | 'preview' | 'analyzing' | 'review' | 'saving' | 'error';
interface ImageMeta { width: number; height: number; size: string }

const formatClock = (seconds?: number) => {
  if (seconds == null) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
};

const parseClock = (value: string) => {
  const parts = value.trim().split(':').map(Number);
  if (parts.some(Number.isNaN)) return undefined;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0];
};

interface FieldProps {
  label: string;
  value?: string | number;
  suffix?: string;
  type?: string;
  issue?: 'warning' | 'error';
  onChange: (value: string) => void;
}

const ReviewField: React.FC<FieldProps> = ({ label, value, suffix, type = 'text', issue, onChange }) => (
  <label className={`review-field ${issue ? `has-${issue}` : ''}`}>
    <span>{label}</span>
    <div>
      <input type={type} value={value ?? ''} onChange={event => onChange(event.target.value)} />
      {suffix && <small>{suffix}</small>}
    </div>
  </label>
);

export const ImageUpload: React.FC<ImageUploadProps> = ({ userId, onWorkoutCreated, onCancel }) => {
  const [state, setState] = useState<UploadState>('select');
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [meta, setMeta] = useState<ImageMeta | null>(null);
  const [draft, setDraft] = useState<WorkoutImportDraft | null>(null);
  const [analysis, setAnalysis] = useState<WorkoutAnalysis | null>(null);
  const [acceptWarnings, setAcceptWarnings] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectFile = (file?: File) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Поддерживаются JPG, PNG и WEBP');
      setState('error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Файл больше 10 МБ. Сохраните его в JPG с высоким качеством.');
      setState('error');
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const image = new Image();
      image.onload = () => {
        setMeta({
          width: image.naturalWidth,
          height: image.naturalHeight,
          size: file.size < 1024 * 1024
            ? `${Math.round(file.size / 1024)} КБ`
            : `${(file.size / 1024 / 1024).toFixed(1)} МБ`
        });
        setPreview(dataUrl);
        setState('preview');
      };
      image.onerror = () => {
        setError('Файл повреждён или не читается');
        setState('error');
      };
      image.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;
    setState('analyzing');
    haptic.medium();
    try {
      const result = await analyzeWorkoutImage(userId, selectedFile);
      setDraft(result);
      setAnalysis(result.analysis);
      setState('review');
      haptic.success();
    } catch (err: any) {
      setError(err?.message || 'Ошибка при анализе изображения');
      setState('error');
      haptic.error();
    }
  };

  const update = <K extends keyof WorkoutAnalysis>(field: K, value: WorkoutAnalysis[K]) => {
    setAnalysis(current => current ? { ...current, [field]: value } : current);
  };
  const updateNumber = (field: keyof WorkoutAnalysis, value: string) =>
    update(field, (value === '' ? undefined : Number(value.replace(',', '.'))) as never);
  const issueFor = (field: string) => draft?.validation.find(issue => issue.field === field)?.level;

  const handleConfirm = async () => {
    if (!draft || !analysis) return;
    setState('saving');
    try {
      const workout = await confirmWorkoutImport(userId, draft.id, analysis, acceptWarnings);
      haptic.success();
      onWorkoutCreated(workout);
    } catch (err: any) {
      const validation = err?.response?.data?.validation;
      if (validation && draft) setDraft({ ...draft, validation, requiresChanges: true });
      setError(err?.message || 'Не удалось сохранить тренировку');
      setState('review');
      haptic.error();
    }
  };

  const discardDraft = async () => {
    if (draft) await cancelWorkoutImport(userId, draft.id).catch(() => undefined);
  };
  const handleCancel = async () => {
    await discardDraft();
    onCancel();
  };
  const handleReset = async () => {
    await discardDraft();
    setState('select');
    setPreview(null);
    setSelectedFile(null);
    setMeta(null);
    setDraft(null);
    setAnalysis(null);
    setAcceptWarnings(false);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="image-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={event => selectFile(event.target.files?.[0])}
        className="image-upload-input"
      />

      {state === 'select' && (
        <div className="upload-select">
          <div className="upload-badge"><span /> Huawei Health · длинный JPG</div>
          <div className="upload-title-block">
            <h2>Добавьте отчёт о пробежке</h2>
            <p>Распознаем дистанцию, пульс, темп, технику и зоны нагрузки.</p>
          </div>
          <button
            className={`upload-dropzone ${dragging ? 'is-dragging' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={event => { event.preventDefault(); setDragging(true); }}
            onDragOver={event => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={event => {
              event.preventDefault();
              setDragging(false);
              selectFile(event.dataTransfer.files?.[0]);
            }}
          >
            <span className="dropzone-icon" aria-hidden="true">＋</span>
            <strong>Выбрать скриншот</strong>
            <span>JPG, PNG или WEBP · до 10 МБ</span>
          </button>
          <div className="upload-feature-list">
            <div><span className="feature-check">✓</span><p><strong>Длинные отчёты</strong><small>Разделим изображение без потери мелкого текста</small></p></div>
            <div><span className="feature-check">✓</span><p><strong>Проверка значений</strong><small>Перед сохранением всё можно исправить</small></p></div>
            <div><span className="feature-check">✓</span><p><strong>Черновик на 24 часа</strong><small>Неподтверждённый файл удалится автоматически</small></p></div>
          </div>
        </div>
      )}

      {state === 'preview' && preview && (
        <div className="upload-preview">
          <div className="preview-heading">
            <div><span className="preview-kicker">Файл готов</span><h2>Проверим тренировку?</h2></div>
            <button className="text-button" onClick={handleReset}>Заменить</button>
          </div>
          <div className="preview-card">
            <div className="preview-viewport"><img src={preview} alt="Превью отчёта" className="preview-image" /></div>
            <div className="preview-details">
              <strong>{selectedFile?.name}</strong>
              <span>{meta?.width} × {meta?.height} · {meta?.size}</span>
              {meta && meta.height / meta.width > 2.5 && <span className="long-report-label">✓ Длинный отчёт определён</span>}
            </div>
          </div>
          <p className="privacy-note">Файл останется черновиком, пока вы не подтвердите показатели.</p>
          <div className="preview-actions">
            <button className="upload-btn primary" onClick={handleAnalyze}>Распознать тренировку</button>
            <button className="upload-btn secondary" onClick={handleCancel}>Отмена</button>
          </div>
        </div>
      )}

      {state === 'analyzing' && (
        <div className="upload-analyzing">
          <div className="analysis-orbit"><span /><span /></div>
          <div className="upload-title-block"><h2>Читаем отчёт</h2><p>Длинный скриншот обрабатывается по частям. Если отчёты загружены подряд, бесплатный ИИ поставит следующий в очередь — обычно до одной минуты.</p></div>
          <div className="analysis-steps">
            <div className="done"><span>✓</span><p>Изображение подготовлено<small>Мелкий текст сохранён</small></p></div>
            <div className="active"><span>2</span><p>Распознаём показатели<small>Пульс, темп, каденс и техника</small></p></div>
            <div><span>3</span><p>Проверяем диапазоны<small>Найдём сомнительные значения</small></p></div>
          </div>
        </div>
      )}

      {(state === 'review' || state === 'saving') && analysis && draft && (
        <div className="review-workout">
          <div className="review-header">
            <div>
              <span className="preview-kicker">Распознано · {Math.round((analysis.confidence ?? 0) * 100)}%</span>
              <h2>Проверьте показатели</h2>
            </div>
            <span className="parts-badge">{draft.imageParts} фрагм.</span>
          </div>
          {draft.validation.length > 0 && (
            <div className="validation-box">
              <strong>{draft.validation.some(item => item.level === 'error') ? 'Нужна проверка' : 'Обратите внимание'}</strong>
              {draft.validation.map((issue, index) => <p key={`${issue.field}-${index}`}>{issue.message}</p>)}
            </div>
          )}
          {error && <p className="review-inline-error">{error}</p>}

          <section className="review-section">
            <h3>Основное</h3>
            <div className="review-grid">
              <ReviewField label="Дата" type="date" value={analysis.date} issue={issueFor('date')} onChange={value => update('date', value)} />
              <ReviewField label="Тип" value={analysis.activityType} issue={issueFor('activityType')} onChange={value => update('activityType', value)} />
              <ReviewField label="Дистанция" suffix="км" value={analysis.distanceKm} issue={issueFor('distanceKm')} onChange={value => updateNumber('distanceKm', value)} />
              <ReviewField label="Длительность" value={formatClock(analysis.durationSeconds)} issue={issueFor('durationSeconds')} onChange={value => update('durationSeconds', parseClock(value))} />
              <ReviewField label="Средний темп" suffix="/км" value={formatClock(analysis.paceAvgSecPerKm)} issue={issueFor('paceAvgSecPerKm')} onChange={value => update('paceAvgSecPerKm', parseClock(value))} />
              <ReviewField label="Лучший темп" suffix="/км" value={formatClock(analysis.paceBestSecPerKm)} issue={issueFor('paceBestSecPerKm')} onChange={value => update('paceBestSecPerKm', parseClock(value))} />
            </div>
          </section>

          <section className="review-section">
            <h3>Нагрузка</h3>
            <div className="review-grid">
              <ReviewField label="Средний пульс" suffix="уд/мин" value={analysis.hrAvg} issue={issueFor('hrAvg')} onChange={value => updateNumber('hrAvg', value)} />
              <ReviewField label="Макс. пульс" suffix="уд/мин" value={analysis.hrMax} issue={issueFor('hrMax')} onChange={value => updateNumber('hrMax', value)} />
              <ReviewField label="Калории" suffix="ккал" value={analysis.calories} issue={issueFor('calories')} onChange={value => updateNumber('calories', value)} />
              <ReviewField label="Восстановление" suffix="ч" value={analysis.recoveryTimeHours} issue={issueFor('recoveryTimeHours')} onChange={value => updateNumber('recoveryTimeHours', value)} />
              <ReviewField label="Аэробная" value={analysis.trainingStressAerobic} issue={issueFor('trainingStressAerobic')} onChange={value => updateNumber('trainingStressAerobic', value)} />
              <ReviewField label="Анаэробная" value={analysis.trainingStressAnaerobic} issue={issueFor('trainingStressAnaerobic')} onChange={value => updateNumber('trainingStressAnaerobic', value)} />
            </div>
          </section>

          <section className="review-section">
            <h3>Техника</h3>
            <div className="review-grid">
              <ReviewField label="Каденс" suffix="шаг/мин" value={analysis.cadenceAvg} issue={issueFor('cadenceAvg')} onChange={value => updateNumber('cadenceAvg', value)} />
              <ReviewField label="Макс. каденс" suffix="шаг/мин" value={analysis.cadenceMax} issue={issueFor('cadenceMax')} onChange={value => updateNumber('cadenceMax', value)} />
              <ReviewField label="Длина шага" suffix="см" value={analysis.strideLengthCm} issue={issueFor('strideLengthCm')} onChange={value => updateNumber('strideLengthCm', value)} />
              <ReviewField label="Контакт" suffix="мс" value={analysis.groundContactAvgMs} issue={issueFor('groundContactAvgMs')} onChange={value => updateNumber('groundContactAvgMs', value)} />
              <ReviewField label="Вертикаль" suffix="см" value={analysis.verticalOscAvgCm} issue={issueFor('verticalOscAvgCm')} onChange={value => updateNumber('verticalOscAvgCm', value)} />
              <ReviewField label="Баланс Л" suffix="%" value={analysis.balanceLeftPct} issue={issueFor('balanceLeftPct')} onChange={value => updateNumber('balanceLeftPct', value)} />
              <ReviewField label="Баланс П" suffix="%" value={analysis.balanceRightPct} issue={issueFor('balanceRightPct')} onChange={value => updateNumber('balanceRightPct', value)} />
            </div>
          </section>

          {draft.validation.some(item => item.level === 'error') && (
            <label className="accept-warning">
              <input type="checkbox" checked={acceptWarnings} onChange={event => setAcceptWarnings(event.target.checked)} />
              <span>Я проверил значения и хочу сохранить их несмотря на предупреждения</span>
            </label>
          )}
          <div className="preview-actions sticky-actions">
            <button
              className="upload-btn primary"
              onClick={handleConfirm}
              disabled={state === 'saving'}
            >
              {state === 'saving' ? 'Сохраняем…' : 'Сохранить тренировку'}
            </button>
            <button className="upload-btn secondary" onClick={handleReset} disabled={state === 'saving'}>Удалить черновик</button>
          </div>
        </div>
      )}

      {state === 'error' && (
        <div className="upload-error">
          <div className="error-icon">!</div>
          <h2>Не получилось распознать</h2>
          <p className="error-message">{error}</p>
          <p className="upload-hint">Лучше загружать исходный скриншот целиком, без пересылки со сжатием.</p>
          <div className="error-actions">
            <button className="upload-btn primary" onClick={handleReset}>Попробовать снова</button>
            <button className="upload-btn secondary" onClick={handleCancel}>Отмена</button>
          </div>
        </div>
      )}
    </div>
  );
};
